import { useEffect } from "react";
import { Link } from "react-router-dom";
import { firstWeFeastMedia, RESUME_URL } from "@/data";

const HERO_IMAGE =
  "https://customer-assets-agu9un31.emergentagent.net/job_63deb20a-37d0-451f-872c-c4c59d1b6116/artifacts/xd7a3hs1_FWF_HOTONES_KEYART.png";

const KEY_CONTRIBUTIONS = [
  "Title and show-package animation",
  "In-episode motion graphics and typography",
  "2D character animation for celebrity-interview beats",
  "Lower thirds, episode cards, and segment titles",
  "Logo animation and brand identity motion",
  "Collaborative delivery across editorial, social, and broadcast",
];

function InlineVideo({ video, big = false }) {
  return (
    <figure
      className={`mnnk-inline-video ${big ? "mnnk-inline-video-big" : ""}`}
      data-testid="fwf-inline-video"
    >
      <video
        src={video.src}
        poster={video.poster}
        controls
        muted
        loop
        playsInline
        autoPlay
        preload="metadata"
      />
      <figcaption className="mnnk-inline-video-caption">
        <span className="mnnk-inline-video-title">{video.label}</span>
      </figcaption>
    </figure>
  );
}

export default function FirstWeFeast() {
  useEffect(() => {
    window.scrollTo(0, 0);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("revealed");
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -100px 0px" }
    );
    document.querySelectorAll(".reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="App thtl-page fwf-page">
      {/* Top Nav */}
      <div className="cs-topnav" data-testid="cs-topnav">
        <Link to="/" className="cs-back" data-testid="cs-back-link">
          <span className="cs-back-arrow">←</span>
          <span>Dan McNamara</span>
        </Link>
        <div className="nav-links">
          <Link to="/#reel">Reel</Link>
          <Link to="/#recent-work">Portfolio Work</Link>
          <Link to="/#work">Selected Work</Link>
          <Link to="/#contact">Contact</Link>
          <a
            href={RESUME_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="nav-resume"
            data-testid="nav-resume"
          >
            Resume
          </a>
        </div>
      </div>

      {/* Hero */}
      <section className="thtl-hero" data-testid="fwf-hero">
        <div className="thtl-hero-grid">
          <div className="thtl-hero-text">
            <p className="thtl-kicker reveal">
              <span className="thtl-pulse"></span>
              Case Study · First We Feast
            </p>
            <h1 className="thtl-headline reveal" data-testid="fwf-headline">
              First We
              <br />
              Feast
            </h1>
            <p className="thtl-tagline reveal">
              Motion design &amp; animation across the First We Feast slate.
            </p>
            <div className="thtl-divider reveal"></div>
            <p className="thtl-summary reveal">
              Years of title animation, show-package design, and in-episode
              motion graphics across <strong>First We Feast</strong>'s roster —
              Hot Ones, Gochi Gang, The Burger Show, and That's Odd, Let's
              Drink It. Celebrity-interview beats, segment titles, logo
              animation, and episode graphics built for a fast editorial
              pipeline.
            </p>
          </div>

          <div
            className="thtl-hero-art fwf-hero-art reveal"
            aria-hidden="true"
          >
            <img src={HERO_IMAGE} alt="Hot Ones title key art" />
          </div>
        </div>
      </section>

      {/* Meta strip */}
      <section
        className="thtl-meta-strip reveal"
        data-testid="fwf-meta-strip"
      >
        <div className="thtl-meta-grid">
          <div className="thtl-meta-item">
            <span className="thtl-meta-label">Client</span>
            <span className="thtl-meta-value">First We Feast</span>
          </div>
          <div className="thtl-meta-item">
            <span className="thtl-meta-label">Role</span>
            <span className="thtl-meta-value">
              Motion Design · Animation
            </span>
          </div>
          <div className="thtl-meta-item">
            <span className="thtl-meta-label">Shows</span>
            <span className="thtl-meta-value">
              Hot Ones · Gochi Gang · The Burger Show · TOLDI
            </span>
          </div>
          <div className="thtl-meta-item">
            <span className="thtl-meta-label">Output</span>
            <span className="thtl-meta-value">
              Titles · Show Packages · Episode Graphics
            </span>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section
        id="overview"
        className="thtl-section reveal"
        data-testid="fwf-overview"
      >
        <div className="thtl-section-header">
          <span className="section-number">01</span>
          <h2 className="thtl-section-title">Overview</h2>
        </div>

        <div className="thtl-process-copy">
          <p className="thtl-lead">
            First We Feast's shows live and die on tone — fast, loose, loud, but
            still crafted. The motion needs to carry a show's personality from
            frame one.
          </p>
          <p>
            Across the slate, the work ranges from flagship show-package
            animation on Hot Ones to spot-animation on individual celebrity
            episodes, through to full title systems for newer formats like
            Gochi Gang, The Burger Show, and That's Odd, Let's Drink It. One
            brand, one editorial voice, four distinct visual registers.
          </p>
        </div>
      </section>

      {/* Role */}
      <section
        id="role"
        className="thtl-section thtl-role reveal"
        data-testid="fwf-role"
      >
        <div className="thtl-section-header">
          <span className="section-number">02</span>
          <h2 className="thtl-section-title">Role</h2>
        </div>

        <div className="thtl-role-body">
          <div className="thtl-role-copy">
            <p>
              Motion designer and animator across the slate — titles, show
              packages, episode graphics, and 2D character animation for
              individual celebrity beats. Working with the First We Feast
              in-house creative team to pitch, animate, and ship on an
              editorial rhythm.
            </p>
            <p>
              Each show on this page represents a different piece of that work:
              flagship animation on Hot Ones, title systems built for new
              formats, and in-episode motion that extends a show's identity
              into every frame.
            </p>
          </div>

          <ul
            className="thtl-contributions"
            aria-label="Key contributions"
            data-testid="fwf-contributions"
          >
            <li className="thtl-contributions-heading">Key Contributions</li>
            {KEY_CONTRIBUTIONS.map((c, i) => (
              <li key={i} className="thtl-contribution">
                <span className="thtl-contribution-dot" aria-hidden="true" />
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Per-show sections */}
      {firstWeFeastMedia.shows.map((show, idx) => {
        const number = String(idx + 3).padStart(2, "0");
        const hasItems = show.items.length > 0;
        return (
          <section
            key={show.id}
            id={show.id}
            className="thtl-section reveal"
            data-testid={`fwf-show-${show.id}`}
          >
            <div className="thtl-section-header">
              <span className="section-number">{number}</span>
              <h2 className="thtl-section-title">{show.name}</h2>
              {show.summary && (
                <span className="thtl-section-meta">{show.summary}</span>
              )}
            </div>

            {hasItems ? (
              show.items.length === 1 ? (
                <InlineVideo video={show.items[0]} big />
              ) : (
                <div
                  className="mnnk-carousel-motion-grid"
                  data-testid={`fwf-grid-${show.id}`}
                >
                  {show.items.map((item, i) => (
                    <InlineVideo key={`${show.id}-${i}`} video={item} />
                  ))}
                </div>
              )
            ) : (
              <p
                className="fwf-pending"
                data-testid={`fwf-pending-${show.id}`}
              >
                More clips coming soon.
              </p>
            )}
          </section>
        );
      })}

      {/* Outro */}
      <section className="thtl-outro reveal" data-testid="fwf-outro">
        <div className="thtl-outro-inner">
          <h3 className="thtl-outro-title">
            Building a title system, show package, or an episodic graphics
            pipeline?
          </h3>
          <div className="thtl-outro-actions">
            <a
              href="mailto:dbmcnamara@gmail.com"
              className="thtl-outro-email"
              data-testid="fwf-outro-email"
            >
              dbmcnamara@gmail.com
            </a>
            <Link
              to="/#recent-work"
              className="thtl-outro-back"
              data-testid="fwf-outro-back-link"
            >
              ← Back to Portfolio Work
            </Link>
          </div>
        </div>
      </section>

      <footer className="site-footer">
        <p>© 2026 Dan McNamara. Available for full-time and freelance work.</p>
      </footer>
    </div>
  );
}
